import * as React from 'react';
import { connect } from 'react-redux';
import { LONG, SHORT } from '../snackbar/snackbarDuration';
import { ApiError } from '../core/redux/api/apiError';
import { defaultMergeProps } from '../core/redux/sugar';
import { IAppState } from '../core/redux/IAppState';
import { showSnackbarAction } from '../snackbar/snackbarAction';
import * as R from 'ramda';
import { ACTION_TYPE } from '../core/redux/actionType';
import { errorsByActionType } from '../core/redux/api/module';
import { fullName } from './module';
import { getResource } from '../language/resources/getResource';


/**
 * GuestSnackbarDispatcher: a component that listens for Redux events that can't be surfaced in the UI any other way
 * than to show a snackbar. This is currently done mostly in GuestCard, but we can actually have a single dispatcher for
 * all Guest Search.
 *
 * How does it know which events can't be surfaced in the UI? That's hard-coded here.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState) => ({
    getPhotoErrors:       errorsByActionType(state, ACTION_TYPE.GET_GUEST_PHOTO_REQUEST),
    setPhotoErrors:       errorsByActionType(state, ACTION_TYPE.SET_GUEST_PHOTO_REQUEST),
    hotlistErrors:        errorsByActionType(state, ACTION_TYPE.IS_ON_HOTLIST_REQUEST),
    entitlementsErrors:   errorsByActionType(state, ACTION_TYPE.GUEST_ENTITLEMENTS_REQUEST),
    assocRfidErrors:      errorsByActionType(state, ACTION_TYPE.ASSOC_RFID_REQUEST),
    submitWaiverErrors:   errorsByActionType(state, ACTION_TYPE.SUBMIT_WAIVER_REQUEST),
    submitLocalRefErrors: errorsByActionType(state, ACTION_TYPE.SUBMIT_LOCAL_REF_REQUEST),
    importGuestErrors:    errorsByActionType(state, ACTION_TYPE.IMPORT_GUEST_REQUEST),
    guestPaymentErrors:   errorsByActionType(state, ACTION_TYPE.GET_GUEST_PAYMENT_REGISTRATION_REQUEST),
    resources:            state.locale,
    importGuestSuccesses: state.importSuccesses.map(guestId => {
      const guest = state.guests.get(guestId);

      return {
        id:        guestId,
        firstName: guest.firstName,
        lastName:  guest.lastName,
        entitlementErrorCount: guest.entitlementErrorCount ?? 0,
      };
    }),
  }),
  (dispatch) => ({
    showSnackbar: (message: string, isError: boolean, timeoutMs: number, priority?: number) => dispatch(
      showSnackbarAction(message, isError, timeoutMs, priority),
    )
  }),
  defaultMergeProps,
  {
    areStatesEqual: (next: IAppState, prev: IAppState) => prev.error === next.error
                                                          && prev.importSuccesses === next.importSuccesses,
  },
)(class GuestSnackbarDispatcher extends React.PureComponent<{
  getPhotoErrors?: ApiError[];
  setPhotoErrors?: ApiError[];
  hotlistErrors?: ApiError[];
  entitlementsErrors?: ApiError[];
  assocRfidErrors?: ApiError[];
  submitWaiverErrors?: ApiError[];
  submitLocalRefErrors?: ApiError[];
  importGuestErrors?: ApiError[];
  guestPaymentErrors?: ApiError[];
  resources: Record<string, any>;
  importGuestSuccesses?: Array<{ id: string; firstName: string; lastName: string; entitlementErrorCount: number; }>;

  showSnackbar?: (message: string, isError: boolean, timeoutMs: number, priority?: number) => void;
}> {
  /**
   * Shows a Snackbar for each element not in prev that is in next. The Snackbar message comes from calling your `makeMsg`
   * function.
   */

  private snackbarDifference = <T extends {}>(
    isError: boolean,
    makeMsg: (element: T) => string,
    prev?: T[],
    next?: T[],
  ) => R.difference(next || [], prev || [])
    .map(item => this.props.showSnackbar!(makeMsg(item), isError, isError ? LONG : SHORT, isError ? 2 : 1));


  public componentDidUpdate(prevProps): void {
    // Get guest photo
    this.snackbarDifference<ApiError>(
      true,
      error => getResource(this.props.resources, 'GUEST_SNACKBAR_FAILED_PHOTO_LOAD', fullName(error.extras!.guest)),
      prevProps.getPhotoErrors,
      this.props.getPhotoErrors!,
    );

    // Set guest photo
    this.snackbarDifference<ApiError>(
      true,
      error => getResource(this.props.resources, 'GUEST_SNACKBAR_FAILED_PHOTO_SET', fullName(error.extras!.guest)),
      prevProps.setPhotoErrors,
      this.props.setPhotoErrors!,
    );

    // isOnHotlist
    this.snackbarDifference<ApiError>(
      true,
      error => getResource(this.props.resources, 'GUEST_SNACKBAR_FAILED_HOTLIST', fullName(error.extras!.guest)),
      prevProps.hotlistErrors,
      this.props.hotlistErrors!,
    );

    // Load Entitlements
    this.snackbarDifference<ApiError>(
      true,
      error => getResource(this.props.resources, 'GUEST_SNACKBAR_FAILED_PASSES', fullName(error.extras!.guest)),
      prevProps.entitlementErrors,
      this.props.entitlementsErrors!
    );

    // Associate RFID
    this.snackbarDifference<ApiError>(
      true,
      error => getResource(this.props.resources, 'GUEST_SNACKBAR_FAILED_LINK', fullName(error.extras!.guest)),
      prevProps.assocRfidErrors,
      this.props.assocRfidErrors!
    );

    // Submit Waiver
    this.snackbarDifference<ApiError>(
      true,
      error => getResource(this.props.resources, 'GUEST_SNACKBAR_FAILED_SUBMIT_WAIVER', fullName(error.extras!.guest)),
      prevProps.submitWaiverErrors,
      this.props.submitWaiverErrors!
    );

    // Submit local entitlement redemption ref
    this.snackbarDifference<ApiError>(
      true,
      error => getResource(this.props.resources, 'GUEST_SNACKBAR_FAILED_SUBMIT_REFERENCE', fullName(error.extras!.guest)),
      prevProps.submitLocalRefErrors,
      this.props.submitLocalRefErrors!
    );

    // Import Guest Errors
    this.snackbarDifference<ApiError>(
      true,
      error => getResource(this.props.resources, 'GUEST_SNACKBAR_FAILED_IMPORT', fullName(error.extras!.guest), error.extras!.errors ?? ""),
      prevProps.importGuestErrors,
      this.props.importGuestErrors!
    );

    // Get Guest Payment Registration Errors
    this.snackbarDifference<ApiError>(
      true,
      error => getResource(this.props.resources, 'GUEST_SNACKBAR_FAILED_PAYMENT_REGISTRATIONS', fullName(error.extras!.guest)),
      prevProps.guestPaymentErrors,
      this.props.guestPaymentErrors!
    );

    // Import Guest Successes
    this.snackbarDifference<{ id: string; firstName: string; lastName: string; entitlementErrorCount: number; }>(
      false,
      model => model.entitlementErrorCount > 0 
        ? getResource(this.props.resources, 'GUEST_SNACKBAR_SUCCESSFUL_IMPORT_WITH_ENTITLEMENT_ERROR', model.firstName, model.lastName, model.entitlementErrorCount)
        : getResource(this.props.resources, 'GUEST_SNACKBAR_SUCCESSFUL_IMPORT', model.firstName, model.lastName),
      prevProps.importGuestSuccesses,
      this.props.importGuestSuccesses!
    );
  }


  public render = () => null; // Non-rendering component
});
